import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import config from '../../config/config';
import CreateInvoice from './CreateInvoice';

interface Invoice {
  id: number;
  invoice_number: string;
  customer_name: string;
  customer_email: string;
  amount: number;
  due_date: string;
  issue_date: string;
  status: 'betald' | 'obetald' | 'försenad' | 'makulerad';
  description: string;
  final_amount: number;
}

const Invoicing = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('alla');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    const checkOverdue = async () => {
      try {
        await fetch(`${config.api.baseUrl}/invoices/check-overdue`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        fetchInvoices(); // Uppdatera listan efter kontroll
      } catch (error) {
        console.error('Fel vid kontroll av förfallna fakturor:', error);
      }
    };

    // Kör kontroll när komponenten laddas
    checkOverdue();

    // Kör kontroll var 5:e minut
    const interval = setInterval(checkOverdue, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte hämta fakturor');
      }

      const data = await response.json();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleStatusChange = async (invoiceId: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/invoices/${invoiceId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Kunde inte uppdatera status');
      }

      await fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'betald':
        return 'success';
      case 'obetald':
        return 'warning';
      case 'försenad':
        return 'error';
      case 'makulerad':
        return 'text.disabled';
      default:
        return 'default';
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'betald':
        return '#e8f5e9'; // ljusgrön
      case 'obetald':
        return '#fff3e0'; // ljusgul
      case 'försenad':
        return '#ffebee'; // ljusröd
      case 'makulerad':
        return '#f5f5f5'; // ljusgrå
      default:
        return 'transparent';
    }
  };

  const filteredInvoices = statusFilter === 'alla'
    ? invoices
    : invoices.filter(invoice => invoice.status === statusFilter);

  const handleCancelInvoice = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setCancelDialogOpen(true);
  };

  const confirmCancelInvoice = async () => {
    if (!selectedInvoice) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/invoices/${selectedInvoice.id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte makulera fakturan');
      }

      await fetchInvoices();
      setCancelDialogOpen(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error cancelling invoice:', error);
    }
  };

  const handleDownloadPDF = async (invoiceId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/invoices/download/${invoiceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte hämta PDF');
      }

      // Skapa en blob från svaret
      const blob = await response.blob();
      
      // Skapa en temporär länk och klicka på den för att starta nedladdningen
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `faktura_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Kunde inte hämta PDF-filen');
    }
  };

  // Lägg till Luhn-algoritm för OCR-nummer
  function generateOCR(invoiceNumber: string): string {
    let sum = 0;
    let isEven = true;
    for (let i = invoiceNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(invoiceNumber[i]);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return invoiceNumber + checkDigit;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', p: 0, m: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Fakturahantering</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Skapa faktura
        </Button>
      </Stack>

      <Box mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filtrera på status</InputLabel>
          <Select
            value={statusFilter}
            label="Filtrera på status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="alla">Alla fakturor</MenuItem>
            <MenuItem value="betald">Betalda</MenuItem>
            <MenuItem value="obetald">Obetalda</MenuItem>
            <MenuItem value="försenad">Försenade</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ width: '100%', minWidth: 0, boxShadow: 0 }}>
          <Table sx={{ minWidth: 1300 }}>
            <TableHead>
              <TableRow>
                <TableCell>Fakturanummer</TableCell>
                <TableCell>OCR</TableCell>
                <TableCell>Kund</TableCell>
                <TableCell>E-post</TableCell>
                <TableCell align="right">Belopp</TableCell>
                <TableCell>Utfärdad</TableCell>
                <TableCell>Förfaller</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow 
                  key={invoice.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: getStatusBackgroundColor(invoice.status)
                  }}
                >
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>{generateOCR(invoice.invoice_number)}</TableCell>
                  <TableCell>{invoice.customer_name}</TableCell>
                  <TableCell>{invoice.customer_email}</TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat('sv-SE', {
                      style: 'currency',
                      currency: 'SEK'
                    }).format(invoice.final_amount)}
                  </TableCell>
                  <TableCell>
                    {new Date(invoice.issue_date).toLocaleDateString('sv-SE')}
                  </TableCell>
                  <TableCell>
                    {new Date(invoice.due_date).toLocaleDateString('sv-SE')}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={invoice.status}
                      onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                      size="small"
                      disabled={invoice.status === 'makulerad'}
                      sx={{ 
                        minWidth: 120,
                        '& .MuiSelect-select': {
                          color: `${getStatusColor(invoice.status)}.main`
                        }
                      }}
                    >
                      <MenuItem value="betald">Betald</MenuItem>
                      <MenuItem value="obetald">Obetald</MenuItem>
                      <MenuItem value="försenad">Försenad</MenuItem>
                      <MenuItem value="makulerad">Makulerad</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleDownloadPDF(invoice.id)}
                      color="primary"
                      size="small"
                      title="Ladda ner PDF"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <Tooltip title="Makulera">
                      <IconButton
                        size="small"
                        onClick={() => handleCancelInvoice(invoice)}
                        disabled={invoice.status === 'makulerad'}
                      >
                        <CancelIcon color={invoice.status === 'makulerad' ? 'disabled' : 'error'} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <CreateInvoice
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onInvoiceCreated={fetchInvoices}
      />

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Bekräfta makulering</DialogTitle>
        <DialogContent>
          Är du säker på att du vill makulera faktura {selectedInvoice?.invoice_number}?
          Detta kan inte ångras.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Avbryt</Button>
          <Button onClick={confirmCancelInvoice} color="error" variant="contained">
            Makulera
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoicing; 